import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap, switchMap } from 'rxjs/operators';
import { Request } from '../../../core/models/request.model';
import { RequestFilter } from '../../../core/models/request-filter.model';
import { RequestStatus } from '../../../core/enums/request-status.enum';
import { ShippingDetail } from '../../../core/models/shipping-detail.model';
import { PrintSite } from '../../../core/enums/print-site.enum';
import { PrintLocation } from '../../../core/enums/print-location.enum';
import { PrinterType } from '../../../core/enums/printer-type.enum';
import { SortOrder } from '../../../core/enums/sort-order.enum';
import { PagedResult } from '../../../core/models/paged-result.model';
import { LoggingService } from '../../../core/services/logging.service';
import { BaseRepository } from './base.repository';
import envConfig from '../../../env-config';
import { RequestDetail } from '../../../core/models/request-detail.model';
import { UserListItem } from '../../../core/models/user-list.model';
import { RequestStatusOption } from '../../../core/models/request-status-option.model';
import { PrintSiteOption } from '../../../core/models/print-site-option.model';
import { PrintLocationDetailOption } from '../../../core/models/print-location-option.model';
import {
  ProcessRequestForUluroRequest,
  ProcessRequestForUluroResult,
  getProcessRequestErrorMessage,
  PrintLocationOption,
} from '../../../core/models/process-request.model';
import { BulkUpdateRequestPayload } from 'src/app/core/models/bulk-update-request.model';

/**
 * API response DTO from backend
 */
interface RequestApiDto {
  id: number;
  status: string;
  createdDate: string | null;
  arNumber: string;
  countryCode: string | null;
  createdBy: string | null;
  docCount: number;
  distroCount: number;
  isExpedited?: boolean;
  assignedUserName: string | null;
  printSiteName: string | null;
  isPrinted?: boolean;
  shippingMethods?: string | null;
  isManualCheck: boolean;
}

/**
 * Backend PagedResult DTO (uses 'page' instead of 'pageNumber')
 */
interface BackendPagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/**
 * API response DTO for file detail from backend
 */
interface FileDetailApiDto {
  id: number;
  fileName: string;
  fileNumber: string | null;
  fileStatusId: number | null;
  fileStatusName: string | null;
  totalDocuments: number | null;
  totalAmount: number | null;
  totalChecks: number | null;
  totalDDAs: number | null;
  isReadyToProcess: boolean | null;
}

/**
 * API response DTO for request detail from backend
 * Matches the enhanced backend RequestDetailDto structure
 */
interface RequestDetailApiDto {
  // Identification
  id: number;
  requestNumber: string;
  arNumber: string;

  // Contact Information
  contactName: string | null;
  contactEmails: string | null;
  contactPhone: string | null;
  customerComments: string | null;

  // User Information
  userId: number;
  userName: string | null;
  assignedUserId: number | null;
  assignedUserName: string | null;

  // Status Information
  requestStatusId: number | null;
  statusName: string | null;

  // Print Configuration
  printSiteId: number | null;
  printSiteName: string | null;
  printLocationId: number | null;
  printLocationName: string | null;
  printerType: string | null;
  sortOrder: string | null;
  printPostage: boolean | null;

  // Processing Options
  sla: boolean | null;
  isSlaFeeWaived: boolean;
  isSpecialHandling: boolean;
  isGarnCorp: boolean;

  // Shipping & Location
  countryCode: string | null;
  shippingMethods: string | null;
  expectedShipDate: string | null;
  isManualCheck: boolean;

  // Audit Trail
  comments: string | null;
  createdDate: string | null;
  createdBy: string | null;
  modifiedDate: string | null;
  modifiedBy: string | null;

  // Related Collections
  files: FileDetailApiDto[];
}

/**
 * API response wrapper for request detail from backend
 * Wraps RequestDetailApiDto in a standard response envelope
 */
interface RequestDetailApiResponse {
  data: RequestDetailApiDto;
  statusCode: number;
  message: string;
  isSuccess: boolean;
  errorType: string | null;
}

/**
 * Filter options DTO from backend
 */
export interface RequestFilterOptions {
  countries: string[];
  printSites: string[];
  statuses: string[];
}

/**
 * Backend response DTO for legacy save request
 * Wraps RequestDto returned by POST /api/v1/request/legacy/save
 */
interface SaveRequestApiResponse {
  isSuccess: boolean;
  message: string | null;
  data: {
    id: number;
    arNumber: string;
    requestGuid: string | null;
    status: string | null;
    createdDate: string | null;
    files: { id: number; fileNumber: string | null; fileName: string | null }[];
  } | null;
}

/**
 * Result returned to callers of saveRequest()
 */
export interface SaveRequestResult {
  requestId: number;
  arNumber: string;
  fileCount: number;
}

/**
 * Company detail DTO from backend
 */
interface CompanyDetailDto {
  arNumber: string;
  companyName: string;
  customerTypeId: number | null;
  customerTypeName: string | null;
}

/**
 * Shipping detail item DTO from API
 * Note: Numeric fields come as strings and are converted during mapping
 */
interface ShippingDetailItemDto {
  companyName: string;
  distributionCode: string;
  distributionCheckCount: string; // Numeric string from API
  rushed: string; // "Yes" or "No"
  shippingProvider: string;
  shippingAmount: string; // Numeric string from API
  shippingAccountNum: string; // Maps to trackingNumber in UI
  editedDate: string; // Maps to completedDate in UI
  deliveryStatus: string; // Maps to shippingStatus in UI
  shippingStatusCode: string;
  deliveryStatusLastUpdated: string;
  estimatedDeliveryDate?: string;
  trackingUrl?: string; // Hyperlink for tracking number
  arNumber?: string;
  attn?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  contactEmail?: string;
  submissionDate?: string;
}

/**
 * Shipping details API response (inner data)
 * Can be either an array or an object with items property for backwards compatibility
 */
type ShippingDetailsData =
  | ShippingDetailItemDto[]
  | { requestId?: number; items: ShippingDetailItemDto[] };

/**
 * Shipping details API response wrapper from backend
 */
interface ShippingDetailsApiResponse {
  data: ShippingDetailsData;
  statusCode: number;
  message: string;
  isSuccess: boolean;
  errorType: string | null;
}

@Injectable({ providedIn: 'root' })
export class RequestsRepository extends BaseRepository {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggingService);
  private readonly apiUrl = envConfig?.bffUrl ?? '';

  /**
   * Map frontend field names to backend field names for sorting
   */
  private readonly sortFieldMap: Record<string, string> = {
    requestNumber: 'id',
    requestDate: 'createdDate',
    assignedTo: 'assignedUserName',
    country: 'countryCode',
    printSite: 'printSiteName',
    documentCount: 'totalDocuments',
    // Other fields use the same name
  };

  /**
   * Map frontend field name to backend field name
   */
  private mapSortField(field: string): string {
    return this.sortFieldMap[field] ?? field;
  }

  getRequests(filter: RequestFilter): Observable<PagedResult<Request>> {
    const searchRequest = this.buildSearchRequest(filter);

    this.logger.debug('RequestsRepository: Fetching requests from API', {
      filter,
      searchRequest,
    });

    return this.http
      .post<BackendPagedResult<RequestApiDto>>(
        `${this.apiUrl}/api/v1/request/search`,
        searchRequest,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(response => this.transformResponse(response)),
        tap(result =>
          this.logger.debug('RequestsRepository: Requests fetched', {
            count: result.items.length,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to fetch requests',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Build search request object from filter criteria
   */
  private buildSearchRequest(filter: RequestFilter): {
    page: number;
    pageSize: number;
    filters?: {
      search?: string;
      status?: string;
      isExpedited?: boolean;
      isPrinted?: boolean;
      country?: string;
      printSite?: string;
      fromDate?: string;
      toDate?: string;
    };
    sortBy?: string;
    sortDescending?: boolean;
  } {
    const searchRequest: {
      page: number;
      pageSize: number;
      filters?: {
        search?: string;
        status?: string;
        isExpedited?: boolean;
        isPrinted?: boolean;
        country?: string;
        printSite?: string;
        fromDate?: string;
        toDate?: string;
      };
      sortBy?: string;
      sortDescending?: boolean;
    } = {
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? 25,
    };

    const filters = this.buildFiltersObject(filter);
    if (Object.keys(filters).length > 0) {
      searchRequest.filters = filters;
    }

    if (filter.sort) {
      const isDescending = filter.sort.startsWith('-');
      const frontendField = isDescending
        ? filter.sort.substring(1)
        : filter.sort;
      const backendField = this.mapSortField(frontendField);
      searchRequest.sortBy = backendField;
      searchRequest.sortDescending = isDescending;
    }

    return searchRequest;
  }

  /**
   * Build filters object from filter criteria
   */
  private buildFiltersObject(
    filter: RequestFilter
  ): Record<string, string | boolean | number | undefined> {
    const filters: Record<string, string | boolean | number | undefined> = {};

    const addStringFilter = (key: string, value: string | null | undefined) => {
      if (value?.trim()) {
        filters[key] = value.trim();
      }
    };

    const addValueFilter = (
      key: string,
      value: boolean | number | undefined | null
    ) => {
      if (value !== undefined && value !== null) {
        filters[key] = value;
      }
    };

    addStringFilter('search', filter.search);
    addStringFilter('status', filter.status);
    addValueFilter('isExpedited', filter.isExpedited);
    addValueFilter('isPrinted', filter.isPrinted);
    addValueFilter('isManualCheck', filter.isManualCheck);
    addStringFilter('country', filter.country);
    addStringFilter('printSite', filter.printSite);
    addStringFilter('requestNumber', filter.requestNumber);
    addStringFilter('requestDate', filter.requestDate);
    addStringFilter('arNumber', filter.arNumber);
    addStringFilter('createdBy', filter.createdBy);
    addStringFilter('assignedUserName', filter.assignedUserName);
    addStringFilter('shippingMethods', filter.shippingMethods);

    // Map distroCount and docCount filters to min/max format
    addValueFilter('distroCountMin', filter.distroCountMin);
    addValueFilter('distroCountMax', filter.distroCountMax);
    addValueFilter('docCountMin', filter.docCountMin);
    addValueFilter('docCountMax', filter.docCountMax);

    // Column search boxes send a single raw value for doc/distro count -
    // treat it as an exact match by setting both min and max to that value.
    // "pending" is a special case meaning distroCount is not yet available (NULL).
    const documentCountText = filter.documentCount?.trim();
    if (documentCountText) {
      const parsedDocCount = Number(documentCountText);
      if (!Number.isNaN(parsedDocCount)) {
        filters['docCountMin'] = parsedDocCount;
        filters['docCountMax'] = parsedDocCount;
      }
    }

    const distributionCountText = filter.distributionCount?.trim();
    if (distributionCountText) {
      if (distributionCountText.toLowerCase() === 'pending') {
        filters['distroCountStatus'] = 'pending';
      } else {
        const parsedDistroCount = Number(distributionCountText);
        if (!Number.isNaN(parsedDistroCount)) {
          filters['distroCountMin'] = parsedDistroCount;
          filters['distroCountMax'] = parsedDistroCount;
        }
      }
    }

    const hasSearch = filter.search?.trim();
    if (!hasSearch) {
      addStringFilter('fromDate', filter.fromDate);
      addStringFilter('toDate', filter.toDate);
    }

    return filters;
  }

  getRequestById(id: string): Observable<Request | null> {
    return this.http
      .get<RequestApiDto>(`${this.apiUrl}/api/v1/request/${id}`, {
        context: this.authContext(),
      })
      .pipe(
        map(dto => this.mapDtoToRequest(dto)),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to fetch request by ID',
            error
          );
          throw error;
        })
      );
  }

  getFilterOptions(): Observable<RequestFilterOptions> {
    this.logger.debug('RequestsRepository: Fetching filter options from API');

    return this.http
      .get<RequestFilterOptions>(
        `${this.apiUrl}/api/v1/request/filter-options`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        tap(result =>
          this.logger.debug('RequestsRepository: Filter options fetched', {
            countries: result.countries.length,
            printSites: result.printSites.length,
            statuses: result.statuses.length,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to fetch filter options',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Get request detail by request number (using numeric ID)
   * Fetches both request and company data
   */
  getRequestDetail(requestNumber: number): Observable<RequestDetail | null> {
    this.logger.debug('RequestsRepository: Fetching request detail from API', {
      requestNumber,
    });

    return this.http
      .get<RequestDetailApiResponse>(
        `${this.apiUrl}/api/v1/request/${requestNumber}`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        switchMap(response => {
          // Extract data from wrapper response
          const dto = response.data;

          if (!dto) {
            this.logger.warn('RequestsRepository: Empty response data', {
              requestNumber,
            });
            return of(null);
          }

          // Map the request detail
          const requestDetail = this.mapDtoToRequestDetail(dto);

          // If we have an AR number, fetch company name
          if (dto.arNumber) {
            return this.http
              .get<{
                data: CompanyDetailDto;
                statusCode: number;
                message: string;
                isSuccess: boolean;
                errorType: string | null;
              }>(`${this.apiUrl}/api/v1/company/${dto.arNumber}`, {
                context: this.authContext(),
              })
              .pipe(
                map(response => ({
                  ...requestDetail,
                  companyName: response.data.companyName,
                })),
                catchError(error => {
                  this.logger.warn(
                    'RequestsRepository: Failed to fetch company name',
                    { arNumber: dto.arNumber, error }
                  );
                  // Return request detail without company name
                  return of(requestDetail);
                })
              );
          }

          // No AR number, return request detail as-is
          return of(requestDetail);
        }),
        tap(result =>
          this.logger.debug('RequestsRepository: Request detail fetched', {
            requestNumber,
            hasData: !!result,
            companyName: result?.companyName,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to fetch request detail',
            error,
            { requestNumber }
          );
          // Return null instead of throwing to allow error handling in component
          return of(null);
        })
      );
  }

  /**
   * Map API DTO to RequestDetail model
   */
  private mapDtoToRequestDetail(dto: RequestDetailApiDto): RequestDetail {
    const requestNumber = dto.id;

    this.logger.debug('RequestsRepository: Mapping request detail', {
      requestNumber,
      hasComments: !!dto.comments,
      commentsLength: dto.comments?.length,
    });

    return {
      requestNumber: requestNumber,
      arNumber: dto.arNumber,
      requestStatus: this.parseStatusEnum(dto.statusName ?? 'Pending'),
      requestStatusId: dto.requestStatusId,
      assignedTo: dto.assignedUserName ?? '',
      assignedUserId: dto.assignedUserId,
      createdBy: dto.createdBy ?? '',
      companyName: '',
      contactName: dto.contactName ?? '',
      country: dto.countryCode ?? '',
      phoneNumber: dto.contactPhone
        ? this.parsePhoneNumber(dto.contactPhone)
        : null,
      emailAddresses: dto.contactEmails ?? '',
      expeditedRequest: dto.sla ?? false,
      waiveExpeditedFee: dto.isSlaFeeWaived,
      specialHandling: dto.isSpecialHandling,
      printPostage: dto.printPostage ?? false,
      printSite: this.parsePrintSiteEnum(dto.printSiteName),
      printSiteId: dto.printSiteId,
      printLocation: this.parsePrintLocationEnum(dto.printLocationName),
      printLocationId: dto.printLocationId,
      printLocationName: dto.printLocationName,
      printerType: dto.printerType ?? 'HP',
      sortOrder: dto.sortOrder ?? 'Backwards',
      fileDetails: dto.files.map(f => this.mapFileDetail(f)),
      comments: '',
      commentsHistory: dto.comments ?? '',
    };
  }

  /**
   * Parse phone number string to number
   */
  private parsePhoneNumber(phone: string): number | null {
    const cleaned = phone.replace(/\D/g, '');
    const parsed = parseInt(cleaned, 10);
    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Map file detail DTO to FileDetail model
   */
  private mapFileDetail(dto: FileDetailApiDto) {
    return {
      id: dto.id,
      fileName: dto.fileName,
      status: this.mapFileStatusIdToEnum(dto.fileStatusId),
      fileStatusName: dto.fileStatusName ?? 'Pending',
      checkCount: dto.totalChecks ?? 0,
      ddaCount: dto.totalDDAs ?? 0,
      docsInFile: dto.totalDocuments ?? 0,
      totalAmountOfDocs: dto.totalAmount ?? 0,
      fileNumber: dto.fileNumber ?? '',
      readyToProcess: dto.isReadyToProcess ?? false,
    };
  }

  /**
   * Map backend file status ID to frontend RequestStatus enum
   * Backend FileStatus table:
   * 1 = Open
   * 2 = Printed
   * 3 = Cancelled
   * 4 = Received
   * 5 = Shipped
   * 6 = Sent
   */
  private mapFileStatusIdToEnum(fileStatusId: number | null): RequestStatus {
    if (!fileStatusId) {
      return RequestStatus.Pending;
    }

    switch (fileStatusId) {
      case 1: // Open
        return RequestStatus.Open;
      case 2: // Printed
        return RequestStatus.Printed;
      case 3: // Cancelled
        return RequestStatus.Cancelled;
      case 4: // Received
        return RequestStatus.Received;
      case 5: // Shipped (treat as Sent)
        return RequestStatus.Sent;
      case 6: // Sent
        return RequestStatus.Sent;
      default:
        this.logger.warn('RequestsRepository: Unknown file status ID', {
          fileStatusId,
        });
        return RequestStatus.Pending;
    }
  }

  /**
   * Generic enum parser - converts string to enum value if valid
   */
  private parseEnum<T extends string>(
    value: string | null,
    enumObject: Record<string, T>
  ): T | null {
    if (!value) return null;
    const lowerValue = value.toLowerCase();
    return Object.values(enumObject).includes(lowerValue as T)
      ? (lowerValue as T)
      : null;
  }

  /**
   * Parse print site string to enum
   */
  private parsePrintSiteEnum(value: string | null): PrintSite | null {
    return this.parseEnum(value, PrintSite);
  }

  /**
   * Parse print location string to enum
   */
  private parsePrintLocationEnum(value: string | null): PrintLocation | null {
    return this.parseEnum(value, PrintLocation);
  }

  /**
   * Parse printer type string to enum
   */
  private parsePrinterTypeEnum(value: string | null): PrinterType | null {
    return this.parseEnum(value, PrinterType);
  }

  /**
   * Parse sort order string to enum
   */
  private parseSortOrderEnum(value: string | null): SortOrder | null {
    return this.parseEnum(value, SortOrder);
  }

  /**
   * Transform API response to frontend model
   */
  private transformResponse(
    response: BackendPagedResult<RequestApiDto>
  ): PagedResult<Request> {
    return {
      items: response.items.map(dto => this.mapDtoToRequest(dto)),
      totalCount: response.totalCount,
      pageNumber: response.page,
      pageSize: response.pageSize,
      totalPages: response.totalPages,
      hasPreviousPage: response.hasPreviousPage,
      hasNextPage: response.hasNextPage,
    };
  }

  /**
   * Map backend DTO to frontend Request model
   */
  private mapDtoToRequest(dto: RequestApiDto): Request {
    return {
      id: dto.id.toString(),
      requestNumber: dto.id.toString(), // Use ID as request number
      status: this.parseStatusEnum(dto.status),
      statusDisplay: dto.status,
      requestDate: dto.createdDate ?? '',
      arNumber: dto.arNumber,
      country: dto.countryCode ?? '',
      createdBy: dto.createdBy ?? '',
      distributionCount: dto.distroCount,
      documentCount: dto.docCount,
      isExpedited: dto.isExpedited ?? false,
      assignedTo: dto.assignedUserName ?? null,
      printSite: dto.printSiteName,
      isPrinted: dto.isPrinted ?? null,
      shippingMethods: dto.shippingMethods ?? '',
      requiresManualCheck: dto.isManualCheck,
    };
  }

  /**
   * Update request details (save changes without processing)
   */
  updateRequest(
    requestId: number,
    updates: {
      printSiteId?: number | null;
      printLocationId?: number | null;
      printerType?: string | null;
      countryCode?: string | null;
      shippingMethods?: string | null;
      isManualCheck?: boolean | null;
      sla?: boolean | null;
      contactName?: string | null;
      contactEmails?: string | null;
      contactPhone?: string | null;
      isSlaFeeWaived?: boolean | null;
      isSpecialHandling?: boolean | null;
      isGarnCorp?: boolean | null;
      newComments?: string | null;
      requestStatusId?: number | null;
      assignedUserId?: number | null;
      assignedByUserId?: number | null;
    },
    modifiedBy: string
  ): Observable<void> {
    this.logger.debug('RequestsRepository: Updating request', {
      requestId,
      updates,
    });

    const payload = {
      printSiteId: updates.printSiteId,
      printLocationId: updates.printLocationId,
      printerType: updates.printerType,
      countryCode: updates.countryCode,
      shippingMethods: updates.shippingMethods,
      isManualCheck: updates.isManualCheck,
      sla: updates.sla,
      contactName: updates.contactName,
      contactEmails: updates.contactEmails,
      contactPhone: updates.contactPhone,
      isSlaFeeWaived: updates.isSlaFeeWaived,
      isSpecialHandling: updates.isSpecialHandling,
      isGarnCorp: updates.isGarnCorp,
      newComments: updates.newComments,
      statusId: updates.requestStatusId,
      assignedUserId: updates.assignedUserId as number,
      assignedByUserId: updates.assignedByUserId,
      modifiedBy: modifiedBy,
    };

    return this.http
      .put<void>(`${this.apiUrl}/api/v1/request/${requestId}`, payload, {
        context: this.authContext(),
      })
      .pipe(
        tap(() =>
          this.logger.debug('RequestsRepository: Request updated successfully')
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to update request',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Update request status
   */
  updateRequestStatus(
    requestId: number,
    statusId: number,
    modifiedBy: string,
    statusName?: string | null
  ): Observable<void> {
    this.logger.debug('RequestsRepository: Updating request status', {
      requestId,
      statusId,
      statusName,
    });

    const payload = {
      statusId,
      modifiedBy,
      statusName,
    };

    return this.http
      .put<void>(`${this.apiUrl}/api/v1/request/${requestId}/status`, payload, {
        context: this.authContext(),
      })
      .pipe(
        tap(() =>
          this.logger.debug('RequestsRepository: Request status updated')
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to update request status',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Assign request to a user
   */
  assignRequest(
    requestId: number,
    assignedUserId: number,
    assignedByUserId: number,
    modifiedBy: string
  ): Observable<void> {
    this.logger.debug('RequestsRepository: Assigning request', {
      requestId,
      assignedUserId,
    });

    const payload = {
      assignedUserId,
      assignedByUserId,
      modifiedBy,
    };

    return this.http
      .put<void>(`${this.apiUrl}/api/v1/request/${requestId}/assign`, payload, {
        context: this.authContext(),
      })
      .pipe(
        tap(() => this.logger.debug('RequestsRepository: Request assigned')),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to assign request',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Process request for Uluro (print processing)
   * Creates Uluro transactions for each file in the request
   */
  processRequestForUluro(
    requestId: number,
    request: ProcessRequestForUluroRequest
  ): Observable<ProcessRequestForUluroResult> {
    this.logger.debug('RequestsRepository: Processing request for Uluro', {
      requestId,
      printLocationId: request.printLocationId,
      printPostage: request.printPostage,
    });

    // Backend expects flat payload with PascalCase properties
    // Matches: FpsApiV2.Api.Models.Requests.ProcessRequestForUluroRequest
    const payload = {
      PrintLocationId: request.printLocationId,
      PrintPostage: request.printPostage,
      PrinterType: request.printerType ?? null,
      SortOrder: request.sortOrder ?? null,
      ProcessingUserId: request.processingUserId,
      ModifiedBy: request.modifiedBy,
    };

    this.logger.debug('RequestsRepository: Payload', { payload });

    return this.http
      .post<ProcessRequestForUluroResult>(
        `${this.apiUrl}/api/v1/request/${requestId}/process-uluro`,
        payload,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        tap(result =>
          this.logger.debug('RequestsRepository: Request processed for Uluro', {
            transactionsCreated: result.transactionsCreated,
            isOutsourcing: result.isOutsourcing,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to process request for Uluro',
            error
          );
          // Extract error code from response if available
          const errorCode = error?.error?.code ?? error?.error?.errorCode;
          const errorMessage = getProcessRequestErrorMessage(errorCode);
          throw new Error(errorMessage);
        })
      );
  }

  /**
   * Get available print locations for processing (for process modal)
   */
  getPrintLocations(): Observable<PrintLocationOption[]> {
    this.logger.debug('RequestsRepository: Fetching print locations');

    return this.http
      .get<PrintLocationOption[]>(`${this.apiUrl}/api/v1/print-locations`, {
        context: this.authContext(),
      })
      .pipe(
        tap(locations =>
          this.logger.debug('RequestsRepository: Print locations fetched', {
            count: locations.length,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to fetch print locations',
            error
          );
          // Return default options if API fails
          return of(this.getDefaultPrintLocations());
        })
      );
  }

  /**
   * Get print locations by print site ID
   */
  getPrintLocationsByPrintSite(
    printSiteId: number
  ): Observable<PrintLocationDetailOption[]> {
    this.logger.debug(
      'RequestsRepository: Fetching print locations by print site',
      { printSiteId }
    );

    return this.http
      .get<PrintLocationDetailOption[]>(
        `${this.apiUrl}/api/v1/print-locations?printSiteId=${printSiteId}`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(locations => locations.filter(l => l.isActive)),
        tap(locations =>
          this.logger.debug(
            'RequestsRepository: Print locations by site fetched',
            {
              count: locations.length,
              printSiteId,
            }
          )
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to fetch print locations by site',
            error
          );
          // Return empty array if API fails
          return of([]);
        })
      );
  }

  /**
   * Get default print locations (fallback when API is unavailable)
   */
  private getDefaultPrintLocations(): PrintLocationOption[] {
    return [
      { id: 1, name: 'APP1', isOutsourcing: false },
      { id: 2, name: 'APP2', isOutsourcing: false },
      { id: 3, name: 'Weston', isOutsourcing: false },
      { id: 4, name: 'Memphis', isOutsourcing: false },
      { id: 5, name: 'Irving', isOutsourcing: true },
    ];
  }

  /**
   * Set/unset request processing flag
   */
  setRequestProcessing(
    requestId: number,
    isProcessing: boolean,
    modifiedBy: string
  ): Observable<void> {
    this.logger.debug('RequestsRepository: Setting request processing flag', {
      requestId,
      isProcessing,
    });

    const payload = {
      isProcessing,
      modifiedBy,
    };

    return this.http
      .put<void>(
        `${this.apiUrl}/api/v1/request/${requestId}/processing`,
        payload,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        tap(() => this.logger.debug('RequestsRepository: Processing flag set')),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to set processing flag',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Parse status string to RequestStatus enum
   */
  private parseStatusEnum(status: string): RequestStatus {
    const statusLower = status.toLowerCase().replace(/\s+/g, '');

    switch (statusLower) {
      case 'pending':
        return RequestStatus.Pending;
      case 'inprogress':
        return RequestStatus.InProgress;
      case 'completed':
      case 'complete':
        return RequestStatus.Completed;
      case 'cancelled':
      case 'canceled':
        return RequestStatus.Cancelled;
      case 'onhold':
        return RequestStatus.OnHold;
      case 'error':
        return RequestStatus.Error;
      default:
        return RequestStatus.Pending;
    }
  }

  /**
   * Get list of active users for assignment dropdown
   */
  getUserList(): Observable<UserListItem[]> {
    this.logger.debug('RequestsRepository: Fetching user list');

    return this.http
      .get<UserListItem[]>(`${this.apiUrl}/api/v1/user-list`, {
        context: this.authContext(),
      })
      .pipe(
        tap(users =>
          this.logger.debug('RequestsRepository: User list fetched', {
            count: users.length,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to fetch user list',
            error
          );
          // Return empty array if API fails
          return of([]);
        })
      );
  }

  /**
   * Get list of request statuses
   */
  getRequestStatuses(): Observable<RequestStatusOption[]> {
    this.logger.debug('RequestsRepository: Fetching request statuses');

    return this.http
      .get<RequestStatusOption[]>(`${this.apiUrl}/api/v1/request-statuses`, {
        context: this.authContext(),
      })
      .pipe(
        map(statuses =>
          statuses
            .filter(s => s.isActive)
            .sort((a, b) => a.displayOrder - b.displayOrder)
        ),
        tap(statuses =>
          this.logger.debug('RequestsRepository: Request statuses fetched', {
            count: statuses.length,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to fetch request statuses',
            error
          );
          // Return empty array if API fails
          return of([]);
        })
      );
  }

  /**
   * Get list of print sites
   */
  getPrintSites(): Observable<PrintSiteOption[]> {
    this.logger.debug('RequestsRepository: Fetching print sites');

    return this.http
      .get<PrintSiteOption[]>(`${this.apiUrl}/api/v1/print-sites`, {
        context: this.authContext(),
      })
      .pipe(
        map(sites => sites.filter(s => s.isActive)),
        tap(sites =>
          this.logger.debug('RequestsRepository: Print sites fetched', {
            count: sites.length,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to fetch print sites',
            error
          );
          // Return empty array if API fails
          return of([]);
        })
      );
  }

  /**
   * Submit a new create-request as JSON.
   * Files are already in Swift temp storage; their paths are included in the metadata.
   */
  saveRequest(body: string): Observable<SaveRequestResult> {
    this.logger.debug('RequestsRepository: Submitting new request');

    return this.http
      .post<SaveRequestApiResponse>(
        `${this.apiUrl}/api/v1/request/legacy/save`,
        body,
        {
          context: this.authContext(),
          headers: { 'Content-Type': 'application/json' },
        }
      )
      .pipe(
        map(response => {
          if (!response.isSuccess || !response.data) {
            throw new Error(response.message ?? 'Request submission failed');
          }
          return {
            requestId: response.data.id,
            arNumber: response.data.arNumber,
            fileCount: response.data.files?.length ?? 0,
          };
        }),
        tap(result =>
          this.logger.debug('RequestsRepository: Request created', {
            requestId: result.requestId,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to submit request',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Get shipping details for a request
   */
  getShippingDetails(requestId: number): Observable<ShippingDetail[]> {
    this.logger.debug('RequestsRepository: Fetching shipping details', {
      requestId,
    });

    return this.http
      .get<ShippingDetailsApiResponse>(
        `${this.apiUrl}/api/v1/request/${requestId}/shipping-details`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(response => this.mapShippingDetailsResponse(response)),
        tap(details =>
          this.logger.debug('RequestsRepository: Shipping details fetched', {
            count: details.length,
            requestId,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to fetch shipping details',
            error,
            { requestId }
          );
          // Return empty array if API fails
          return of([]);
        })
      );
  }

  /**
   * Parse date string, return null if invalid
   */
  private parseDate(dateString: string): Date | null {
    if (!dateString || typeof dateString !== 'string') {
      return null;
    }
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Map API shipping details response to ShippingDetail model
   */
  private mapShippingDetailsResponse(
    response: ShippingDetailsApiResponse
  ): ShippingDetail[] {
    // Extract data from wrapper response
    // Data can be either an array directly or an object with items property
    const items = Array.isArray(response.data)
      ? response.data
      : response.data?.items;

    if (!items || items.length === 0) {
      this.logger.warn('RequestsRepository: Empty shipping details response');
      return [];
    }

    return items.map(item => ({
      companyName: item.companyName,
      distributionCenter: item.distributionCode ?? '',
      checkCount: parseInt(item.distributionCheckCount ?? '0', 10),
      expedited: item.rushed === 'Yes',
      shippingProvider: item.shippingProvider,
      shippingAmount: parseFloat(String(item.shippingAmount) || '0'),
      completedDate: item.editedDate ? this.parseDate(item.editedDate) : null,
      trackingNumber: item.shippingAccountNum ?? null,
      trackingUrl: item.trackingUrl ?? null,
      shippingStatus: item.deliveryStatus ?? '',
      lastUpdated: item.deliveryStatusLastUpdated
        ? (this.parseDate(item.deliveryStatusLastUpdated) ?? new Date())
        : new Date(),
      estDeliveryDate: item.estimatedDeliveryDate
        ? this.parseDate(item.estimatedDeliveryDate)
        : null,
      rushed: item.rushed,
      shippingStatusCode: item.shippingStatusCode,
      deliveryStatusLastUpdated: item.deliveryStatusLastUpdated
        ? this.parseDate(item.deliveryStatusLastUpdated)
        : null,
      arNumber: item.arNumber,
      attn: item.attn,
      address1: item.address1,
      address2: item.address2,
      city: item.city,
      state: item.state,
      zip: item.zip,
      contactEmail: item.contactEmail,
      submissionDate: item.submissionDate,
    }));
  }

  /**
   * Update shipping details for a request
   */
  updateShippingDetails(requestId: number): Observable<ShippingDetail[]> {
    this.logger.debug('RequestsRepository: Updating shipping details', {
      requestId,
    });

    return this.http
      .put<ShippingDetailsApiResponse>(
        `${this.apiUrl}/api/v1/request/${requestId}/shipping-details`,
        {},
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(response => this.mapShippingDetailsResponse(response)),
        tap(details =>
          this.logger.debug('RequestsRepository: Shipping details updated', {
            count: details.length,
            requestId,
          })
        ),
        catchError(error => {
          this.logger.error(
            'RequestsRepository: Failed to update shipping details',
            error,
            { requestId }
          );
          return of([]);
        })
      );
  }

  bulkUpdateRequests(payload: BulkUpdateRequestPayload): Observable<void> {
    const apiUrl = `${this.apiUrl}/api/v1/request/bulk`;

    return this.http
      .post<void>(apiUrl, payload, {
        context: this.authContext(),
      })
      .pipe(
        tap(() => {
          this.logger.info('RequestsRepository: Bulk update successful');
        }),
        catchError(error => {
          this.logger.error('RequestsRepository: Bulk update failed', error);
          throw error;
        })
      );
  }
}
